import { useCallback, useEffect, useRef, useState } from 'react';
import { createCommands } from '../commands';
import './Terminal.css';
import SnakeGame from './SnakeGame';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function randHex(len) {
  return Array.from({ length: len }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

function mac() {
  return Array.from({ length: 6 }, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join(':');
}

export default function Terminal() {
  const shellRef = useRef(null);
  const bodyRef = useRef(null);
  const outputRef = useRef(null);
  const inputRef = useRef(null);
  const typedRef = useRef(null);
  const commandLineRef = useRef(null);
  const clockRef = useRef(null);
  const matrixCanvasRef = useRef(null);
  const biosRef = useRef(null);
  const reducedMotionRef = useRef(false);

  const [current, setCurrent] = useState('');
  const [terminated, setTerminated] = useState(false);
  const matrixOnRef = useRef(false);
  const [snakeActive, setSnakeActive] = useState(false);
  const snakeActiveRef = useRef(false);
  const matrixRafRef = useRef(null);
  const historyRef = useRef([]);
  const historyIdxRef = useRef(-1);
  const bootStartedRef = useRef(false);
  const bootedRef = useRef(false);
  const commandsRef = useRef(null);
  const inCommandRef = useRef(false);
  const lineIndexRef = useRef(0);
  const batchIdRef = useRef(0);
  const currentBatchRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (bodyRef.current) {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      }
    });
  }, []);

  const clearOutput = useCallback(() => {
    if (outputRef.current) {
      outputRef.current.innerHTML = '';
    }
  }, []);

  const print = useCallback((text, cls = '') => {
    if (!outputRef.current) return;
    const line = document.createElement('div');
    line.className = cls ? `line ${cls}` : 'line';
    line.textContent = text;
    if (inCommandRef.current) {
      line.classList.add('power-on');
      line.style.setProperty('--line-index', String(lineIndexRef.current));
      line.style.setProperty('--batch-id', String(currentBatchRef.current));
      lineIndexRef.current += 1;
    }
    outputRef.current.appendChild(line);
    scrollToBottom();
  }, [scrollToBottom]);

  const printLine = useCallback((text, cls = '') => {
    print(text, cls);
  }, [print]);

  const appendHtml = useCallback((html) => {
    if (!outputRef.current) return;
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    if (inCommandRef.current) {
      wrap.classList.add('power-on-block');
      wrap.style.setProperty('--line-index', String(lineIndexRef.current));
      wrap.style.setProperty('--batch-id', String(currentBatchRef.current));
      lineIndexRef.current += 1;
    }
    outputRef.current.appendChild(wrap);
    scrollToBottom();
  }, [scrollToBottom]);

  const focusInput = useCallback(() => {
    if (!bootedRef.current || terminated || snakeActiveRef.current) return;
    if (inputRef.current) {
      try {
        inputRef.current.focus({ preventScroll: true });
      } catch {
        inputRef.current.focus();
      }
    }
    if (commandLineRef.current) {
      commandLineRef.current.classList.add('active');
    }
  }, [terminated]);

  const stopMatrix = useCallback(() => {
    if (matrixRafRef.current) {
      cancelAnimationFrame(matrixRafRef.current);
      matrixRafRef.current = null;
    }
    const ctx = matrixCanvasRef.current?.getContext('2d');
    if (ctx && matrixCanvasRef.current) {
      ctx.clearRect(0, 0, matrixCanvasRef.current.width, matrixCanvasRef.current.height);
    }
    if (matrixCanvasRef.current) {
      matrixCanvasRef.current.classList.remove('on');
    }
    matrixOnRef.current = false;
    return false;
  }, []);

  const startMatrix = useCallback(() => {
    if (!matrixCanvasRef.current || matrixRafRef.current) return;
    const canvas = matrixCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const fontSize = 14;
    let columns = Math.floor(canvas.width / fontSize);
    let drops = Array(columns).fill(1);

    const draw = () => {
      ctx.fillStyle = 'rgba(7, 7, 8, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#00ff41';
      ctx.font = `${fontSize}px 'JetBrains Mono', monospace`;

      for (let i = 0; i < drops.length; i++) {
        const char = String.fromCharCode(0x30a0 + Math.random() * 96);
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
      matrixRafRef.current = requestAnimationFrame(draw);
    };

    draw();
    matrixCanvasRef.current.classList.add('on');
    matrixOnRef.current = true;
    return true;
  }, []);

  const toggleMatrix = useCallback(() => {
    if (matrixOnRef.current) {
      stopMatrix();
      return false;
    }
    startMatrix();
    return true;
  }, [startMatrix, stopMatrix]);

  const submitCommand = useCallback((raw) => {
    const cmd = raw.trim();
    if (!cmd) return;

    const echo = document.createElement('div');
    echo.className = 'command-line';
    echo.innerHTML = `<span class="prompt">stijn@stijn.sh:~$ </span><span class="typed">${raw.replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c])}</span>`;
    if (outputRef.current) {
      outputRef.current.appendChild(echo);
    }

    historyRef.current.push(cmd);
    historyIdxRef.current = historyRef.current.length;
    setCurrent('');

    const [name] = cmd.split(/\s+/);
    const lower = name.toLowerCase();

    if (lower === 'snake') {
      snakeActiveRef.current = true;
      setSnakeActive(true);
      return;
    }

    inCommandRef.current = true;
    lineIndexRef.current = 0;
    batchIdRef.current += 1;
    currentBatchRef.current = batchIdRef.current;

    try {
      if (commandsRef.current?.[lower]) {
        try {
          commandsRef.current[lower].run();
        } catch (e) {
          print(`  error: ${e.message}`, 'error');
        }
      } else if (lower === 'whoami') {
        print('');
        print('Stijn van den Broek', 'cyan');
        print('Data Science Consultant & Technical Trainer', 'dim');
      } else if (lower === 'date') {
        print(new Date().toString(), 'dim');
      } else if (lower === 'ls') {
        print(Object.keys(commandsRef.current || {}).sort().join('  '), 'dim');
      } else {
        print(`  command not found: ${lower}`, 'error');
        print('  type "help" for a list of commands', 'dim');
      }
    } finally {
      inCommandRef.current = false;
      currentBatchRef.current = null;
    }

    scrollToBottom();
  }, [print, scrollToBottom]);

  const runBootSequence = useCallback(async () => {
    bootedRef.current = false;
    if (inputRef.current) inputRef.current.disabled = true;
    if (commandLineRef.current) commandLineRef.current.classList.add('boot-hidden');
    clearOutput();

    let bootTime = 0.0;
    const stamp = () => {
      bootTime += Math.random() * 0.04 + 0.01;
      return `[${bootTime.toFixed(6).padStart(12, '0')}]`;
    };

    const now = new Date();
    const biosDate = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${String(now.getFullYear()).slice(-2)}`;

    const lines = [
      { t: () => `${stamp()} stijn.sh bootloader v2.4`, c: 'dim', d: 60 },
      { t: () => `${stamp()} BIOS date ${biosDate} ${now.toTimeString().slice(0, 8)} Ver 1.0`, c: 'dim', d: 50 },
      { t: () => `${stamp()} CPU: stijn-core i9 @ 4.20GHz`, c: 'dim', d: 60 },
      { t: () => `${stamp()} Detected 16384 MB of RAM`, c: 'dim', d: 50 },
      { t: () => `${stamp()} Loading kernel image ...`, c: 'dim', d: 120 },
      { t: () => `${stamp()} Linux version 6.10.0-stijn-custom (build@stijn.sh) (gcc 14.1.1) #1 SMP PREEMPT_DYNAMIC`, c: 'dim', d: 40 },
      { t: () => `${stamp()} Command line: root=/dev/terminal rw quiet splash console=ttyS0`, c: 'dim', d: 40 },
      { t: () => `${stamp()} x86/cpu: CPU features: fpu vme de pse tsc msr pae mce cx8 apic sep mtrr pge mca cmov pat pse36 clflush mmx fxsr sse sse2 ht syscall nx rdtscp lm constant_tsc rep_good nopl xtopology`, c: 'dim', d: 40 },
      { t: () => `${stamp()} BIOS-provided physical RAM map:`, c: 'dim', d: 30 },
      { t: () => `${stamp()} BIOS-e820: [mem 0x0000000000000000-0x000000000009ffff] usable`, c: 'dim', d: 30 },
      { t: () => `${stamp()} BIOS-e820: [mem 0x0000000000100000-0x000000003ffeffff] usable`, c: 'dim', d: 30 },
      { t: () => `${stamp()} ACPI: RSDP 0x${randHex(16)} 000024 (v02 VBOX  )`, c: 'dim', d: 30 },
      { t: () => `${stamp()} ACPI: XSDT 0x${randHex(16)} 0000AC (v01 VBOX   VBOXXSDT 00000001 ASL  00000061)`, c: 'dim', d: 30 },
      { t: () => `${stamp()} ACPI: FACP 0x${randHex(16)} 0000F4 (v04 VBOX   VBOXFACP 00000001 ASL  00000061)`, c: 'dim', d: 30 },
      { t: () => `${stamp()} Zone ranges:`, c: 'dim', d: 30 },
      { t: () => `${stamp()}   DMA      [mem 0x0000000000001000-0x0000000000ffffff]`, c: 'dim', d: 20 },
      { t: () => `${stamp()}   DMA32    [mem 0x0000000001000000-0x000000003ffeffff]`, c: 'dim', d: 20 },
      { t: () => `${stamp()}   Normal   empty`, c: 'dim', d: 20 },
      { t: () => `${stamp()} DMI: innotek GmbH VirtualBox/VirtualPlatform, BIOS VirtualBox 6.1.50 ${biosDate}`, c: 'dim', d: 40 },
      { t: () => `${stamp()} tsc: Detected 4200.000 MHz processor`, c: 'dim', d: 30 },
      { t: () => `${stamp()} pid_max: default: 32768 minimum: 301`, c: 'dim', d: 20 },
      { t: () => `${stamp()} Mounting root filesystem ... [ OK ]`, c: 'green', d: 80 },
      { t: () => `${stamp()} fsck: /dev/sda1: 12% completed ...`, c: 'dim', d: 60 },
      { t: () => `${stamp()} fsck: /dev/sda1: 38% completed ...`, c: 'dim', d: 60 },
      { t: () => `${stamp()} fsck: /dev/sda1: 71% completed ...`, c: 'dim', d: 60 },
      { t: () => `${stamp()} fsck: /dev/sda1: 100% completed ... [ OK ]`, c: 'green', d: 80 },
      { t: () => `${stamp()} Mounting /home/stijn ... [ OK ]`, c: 'green', d: 60 },
      { t: () => `${stamp()} Mounting /data ... [ OK ]`, c: 'green', d: 60 },
      { t: () => `${stamp()} udev: starting version 255`, c: 'dim', d: 50 },
      { t: () => `${stamp()} input: AT Translated Set 2 keyboard as /devices/platform/i8042/serio0/input/input0`, c: 'dim', d: 40 },
      { t: () => `${stamp()} input: VirtualBox USB Tablet as /devices/pci0000:00/.../input/input1`, c: 'dim', d: 40 },
      { t: () => `${stamp()} usb 1-1: new high-speed USB device number 2 using xhci_hcd`, c: 'dim', d: 40 },
      { t: () => `${stamp()} usb 1-1: New USB device found, idVendor=80ee, idProduct=0021`, c: 'dim', d: 30 },
      { t: () => `${stamp()} e1000: eth0 NIC Link is Up 1000 Mbps Full Duplex`, c: 'dim', d: 40 },
      { t: () => `${stamp()} e1000: eth0 MAC ${mac()}`, c: 'dim', d: 40 },
      { t: () => `${stamp()} eth0: assigned 10.0.2.15/24`, c: 'dim', d: 40 },
      { t: () => `${stamp()} random: crng init done`, c: 'dim', d: 30 },
      { t: () => `${stamp()} systemd[1]: systemd 255 running in system mode (+PAM +AUDIT ...)`, c: 'dim', d: 40 },
      { t: () => `${stamp()} systemd[1]: hostname set to <stijn.sh>`, c: 'dim', d: 40 },
      { t: () => `${stamp()} systemd[1]: Mounted /dev/hugepages.`, c: 'dim', d: 20 },
      { t: () => `${stamp()} systemd[1]: Started kerneloops.service.`, c: 'dim', d: 20 },
      { t: () => `${stamp()} systemd[1]: Started sshd.service ... [ OK ]`, c: 'green', d: 50 },
      { t: () => `${stamp()} sshd[412]: Server listening on 0.0.0.0 port 22.`, c: 'dim', d: 30 },
      { t: () => `${stamp()} systemd[1]: Started cron.service ... [ OK ]`, c: 'green', d: 40 },
      { t: () => `${stamp()} systemd[1]: Started nginx.service ... [ OK ]`, c: 'green', d: 40 },
      { t: () => `${stamp()} systemd[1]: Started matrix-rain.service ... [ OK ]`, c: 'green', d: 60 },
      { t: () => `${stamp()} systemd[1]: Started stijn-portfolio.service ... [ OK ]`, c: 'green', d: 80 },
      { t: () => `${stamp()} systemd[1]: Reached target network.target.`, c: 'dim', d: 40 },
      { t: () => `${stamp()} systemd[1]: Reached target multi-user.target.`, c: 'dim', d: 40 },
      { t: () => `${stamp()} systemd[1]: Startup finished in 2.142s (kernel) + 0.857s (userspace) = 2.999s.`, c: 'dim', d: 80 },
      { t: 'stijn.sh login: stijn', c: '', d: 200 },
      { t: 'Password: ********', c: 'dim', d: 200 },
      { t: `Last login: ${now.toString()}`, c: 'dim', d: 100 },
      { t: '', c: '', d: 40 },
      { t: 'Welcome. Type "help" to list commands.', c: 'cyan', d: 100 },
      { t: '', c: '', d: 40 },
    ];

    for (const item of lines) {
      const text = typeof item.t === 'function' ? item.t() : item.t;
      if (text !== '' || item.c) print(text, item.c || '');
      else print('');
      const delay = reducedMotionRef.current ? 0 : item.d || 40;
      if (delay) await sleep(delay);
    }

    await sleep(reducedMotionRef.current ? 150 : 600);
    clearOutput();
    print('Welcome. Type "help" to list commands.', 'cyan');
    if (commandLineRef.current) commandLineRef.current.classList.remove('boot-hidden');
    if (inputRef.current) inputRef.current.disabled = false;
    bootedRef.current = true;
    setTimeout(focusInput, 0);
  }, [clearOutput, focusInput, print]);

  const startBoot = useCallback(async () => {
    if (bootStartedRef.current) return;
    bootStartedRef.current = true;
    if (biosRef.current) biosRef.current.classList.add('hidden');
    await runBootSequence();
  }, [runBootSequence]);

  const reboot = useCallback(() => {
    bootStartedRef.current = false;
    setTerminated(false);
    stopMatrix();
    startBoot();
  }, [startBoot, stopMatrix]);

  const terminate = useCallback(() => {
    setTerminated(true);
    if (commandLineRef.current) commandLineRef.current.classList.remove('active');
    if (inputRef.current) inputRef.current.blur();
  }, []);

  // Initialize commands context after refs are set up
  useEffect(() => {
    commandsRef.current = createCommands({
      print,
      printLine,
      appendHtml,
      clearOutput,
      toggleMatrix,
      reboot,
      terminate,
    });
  }, [print, printLine, appendHtml, clearOutput, toggleMatrix, reboot, terminate]);

  // Clock
  useEffect(() => {
    const updateClock = () => {
      if (clockRef.current) {
        const now = new Date();
        clockRef.current.textContent = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
      }
    };
    updateClock();
    const id = setInterval(updateClock, 1000);
    return () => clearInterval(id);
  }, []);

  // Reduced motion + matrix canvas sizing
  useEffect(() => {
    reducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resizeMatrix = () => {
      if (matrixCanvasRef.current) {
        matrixCanvasRef.current.width = window.innerWidth;
        matrixCanvasRef.current.height = window.innerHeight;
      }
    };

    resizeMatrix();
    window.addEventListener('resize', resizeMatrix);
    return () => {
      window.removeEventListener('resize', resizeMatrix);
      stopMatrix();
    };
  }, [stopMatrix]);

  // Boot sequence start
  useEffect(() => {
    const autoStart = setTimeout(startBoot, reducedMotionRef.current ? 0 : 2200);
    const onFirstInteraction = () => {
      clearTimeout(autoStart);
      startBoot();
    };
    document.addEventListener('keydown', onFirstInteraction, { once: true });
    document.addEventListener('click', onFirstInteraction, { once: true });

    return () => {
      clearTimeout(autoStart);
      document.removeEventListener('keydown', onFirstInteraction);
      document.removeEventListener('click', onFirstInteraction);
    };
  }, [startBoot]);

  // Input handling
  useEffect(() => {
    if (terminated) return;

    const shell = shellRef.current;
    const body = bodyRef.current;
    const hiddenInput = inputRef.current;
    const commandLine = commandLineRef.current;

    if (!shell || !body || !hiddenInput || !commandLine) return;

    const handleShellClick = () => focusInput();
    const handleBodyClick = (e) => {
      if (e.target !== body) return;
      focusInput();
    };

    const handleInput = () => {
      setCurrent(hiddenInput.value);
    };

    const handleKeyDown = (e) => {
      const isSnakeGameKey = snakeActiveRef.current && (
        e.key.startsWith('Arrow') || 'wasdWASD '.includes(e.key) || 'pPrRqQ'.includes(e.key) || e.key === 'Escape'
      );
      if (isSnakeGameKey) return;
      if (e.key === 'Enter') {
        const val = hiddenInput.value;
        hiddenInput.value = '';
        submitCommand(val);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (historyIdxRef.current > 0) {
          historyIdxRef.current -= 1;
          const value = historyRef.current[historyIdxRef.current] || '';
          hiddenInput.value = value;
          setCurrent(value);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (historyIdxRef.current < historyRef.current.length - 1) {
          historyIdxRef.current += 1;
          const value = historyRef.current[historyIdxRef.current];
          hiddenInput.value = value;
          setCurrent(value);
        } else {
          historyIdxRef.current = historyRef.current.length;
          hiddenInput.value = '';
          setCurrent('');
        }
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const prefix = hiddenInput.value.toLowerCase();
        const matches = Object.keys(commandsRef.current || {}).filter((c) =>
          c.startsWith(prefix)
        );
        if (matches.length === 1) {
          hiddenInput.value = matches[0];
          setCurrent(matches[0]);
        } else if (matches.length > 1) {
          print('');
          print(matches.join('  '), 'dim');
        }
      } else if (e.key === 'l' && e.ctrlKey) {
        e.preventDefault();
        clearOutput();
      } else if (e.key === 'c' && e.ctrlKey) {
        e.preventDefault();
        hiddenInput.value = '';
        setCurrent('');
        print('^C', 'dim');
      }
    };

    const handleFocusIn = (e) => {
      if (e.target === hiddenInput) {
        commandLine.classList.add('active');
      }
    };
    const handleFocusOut = (e) => {
      if (e.target === hiddenInput) {
        commandLine.classList.remove('active');
      }
    };

    const handleWheel = (e) => {
      if (e.target === document.body || e.target === shell) {
        e.preventDefault();
      }
    };

    shell.addEventListener('click', handleShellClick);
    body.addEventListener('click', handleBodyClick);
    hiddenInput.addEventListener('input', handleInput);
    hiddenInput.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      shell.removeEventListener('click', handleShellClick);
      body.removeEventListener('click', handleBodyClick);
      hiddenInput.removeEventListener('input', handleInput);
      hiddenInput.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('wheel', handleWheel);
    };
  }, [terminated, clearOutput, focusInput, print, submitCommand]);

  // Sync typed display
  useEffect(() => {
    if (typedRef.current) {
      typedRef.current.textContent = current;
    }
  }, [current]);

  // Initial focus attempt
  useEffect(() => {
    const id = setTimeout(focusInput, 100);
    return () => clearTimeout(id);
  }, [focusInput]);
  // Sync snakeActive ref with state
  useEffect(() => {
    snakeActiveRef.current = snakeActive;
    if (snakeActive && inputRef.current) {
      inputRef.current.blur();
    }
  }, [snakeActive]);


  return (
    <>
      <a href="#terminal-body" className="skip-link">
        Skip to terminal
      </a>

      <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
        <defs>
          <filter id="rgbShift" x="-20%" y="-20%" width="140%" height="140%">
            <feColorMatrix type="matrix" in="SourceGraphic" result="red"
              values="1 0 0 0 0
                      0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0" />
            <feOffset in="red" dx="-4" dy="0" result="red-shifted" />
            <feColorMatrix type="matrix" in="SourceGraphic" result="green"
              values="0 0 0 0 0
                      0 1 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0" />
            <feOffset in="green" dx="0" dy="0" result="green-shifted" />
            <feColorMatrix type="matrix" in="SourceGraphic" result="blue"
              values="0 0 0 0 0
                      0 0 0 0 0
                      0 0 1 0 0
                      0 0 0 1 0" />
            <feOffset in="blue" dx="4" dy="0" result="blue-shifted" />
            <feMerge>
              <feMergeNode in="red-shifted" />
              <feMergeNode in="green-shifted" />
              <feMergeNode in="blue-shifted" />
            </feMerge>
          </filter>

          <filter id="displacementFilter" x="-20%" y="-20%" width="140%" height="140%">
            <feTurbulence type="turbulence" baseFrequency="0.05 0.005" numOctaves="2" result="turbulence" seed="3" />
            <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="28" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <div className="terminal-shell" ref={shellRef} data-od-id="terminal-shell">
        <div className="grid" aria-hidden="true" />
        <div className="scanlines" aria-hidden="true" />
        <div className="noise" aria-hidden="true" />
        <div className="vignette" aria-hidden="true" />
        <canvas className="matrix-canvas" ref={matrixCanvasRef} aria-hidden="true" />
        <div className="crt-beam" aria-hidden="true" />

        <div className="terminal-frame" data-od-id="terminal-frame">
          <header className="terminal-header" data-od-id="terminal-header">
            <div className="terminal-header-left">
              <span className="terminal-dot close" aria-hidden="true" />
              <span className="terminal-dot minimize" aria-hidden="true" />
              <span className="terminal-dot maximize" aria-hidden="true" />
              <span className="terminal-title">stijn@stijn.sh</span>
            </div>
            <span className="terminal-meta" ref={clockRef}>00:00:00</span>
          </header>

          <div className="terminal-body" id="terminal-body" ref={bodyRef} data-od-id="terminal-body">
            <input
              className="hidden-input"
              ref={inputRef}
              type="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              aria-label="Terminal input"
              disabled
            />

            <div className="bios-screen" ref={biosRef} id="bios">
              <div className="bios-logo">stijn.sh</div>
              <div className="bios-line">BIOS v2.4</div>
              <div className="bios-line dim">Copyright (C) 2026 Stijn van den Broek</div>
              <div className="bios-line dim">All rights reserved.</div>
              <div className="bios-line dim">CPU: stijn-core i9 @ 4.20 GHz</div>
              <div className="bios-line dim">RAM: 16384 MB</div>
              <div className="bios-line dim">HDD: 512 GB SSD</div>
              <div className="bios-prompt">
                Press any key to boot<span className="bios-cursor" />
              </div>
            </div>

            <div className="output" ref={outputRef} data-od-id="terminal-output" />

            {!snakeActive && (
              <div className="command-line active boot-hidden" ref={commandLineRef} data-od-id="command-line">
                <span className="prompt" id="prompt">stijn@stijn.sh:~$ </span>
                <span className="typed" ref={typedRef} aria-hidden="true" />
                <span className="cursor" aria-hidden="true" />
              </div>
            )}
            {snakeActive && (
              <div className="snake-game-wrapper">
                <SnakeGame onExit={() => { snakeActiveRef.current = false; setSnakeActive(false); setTimeout(focusInput, 0); }} />
              </div>
            )}
          </div>
          <div className="crt-overlay" aria-hidden="true" data-od-id="crt-overlay" />
        </div>
      </div>
    </>
  );
}
